import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalysis } from '../context/AnalysisContext';
import Header from '../components/Header';
import LogService, { LOG_ACTIONS } from '../services/LogService';

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_SIZE_MB = 4;

function Upload() {
  const navigate = useNavigate();
  const { setUploadedFile } = useAnalysis();
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);

  const handleFile = useCallback((selectedFile) => {
    if (!selectedFile) return;
    if (!ACCEPTED_TYPES.includes(selectedFile.type)) {
      alert('Formato não suportado. Envie PDF, JPG ou PNG.');
      return;
    }
    if (selectedFile.size > MAX_SIZE_MB * 1024 * 1024) {
      alert(`Arquivo muito grande. Tamanho máximo: ${MAX_SIZE_MB}MB.`);
      return;
    }
    setFile(selectedFile);
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleInputChange = (e) => {
    handleFile(e.target.files[0]);
  };

  const handleContinue = () => {
    LogService.log(LOG_ACTIONS.UPLOAD, {
      details: { fileName: file.name, fileSize: file.size, fileType: file.type },
    });
    setUploadedFile(file);
    navigate('/especificacoes');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-3xl mx-auto px-4 py-8 md:py-16">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-2">
          Envie sua planta
        </h1>
        <p className="text-gray-500 text-center text-lg mb-10">
          Arraste o arquivo da planta ou clique para selecionar
        </p>

        <label
          htmlFor="file-input"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            block w-full min-h-[300px] md:min-h-[400px] rounded-3xl border-3 border-dashed
            cursor-pointer transition-all duration-300 flex flex-col items-center justify-center
            ${isDragging
              ? 'border-orca-600 bg-orca-50 scale-[1.02]'
              : file
                ? 'border-green-400 bg-green-50'
                : 'border-gray-300 bg-white hover:border-orca-400 hover:bg-orca-50/30'
            }
          `}
          style={{ display: 'flex' }}
        >
          {file ? (
            <div className="flex flex-col items-center gap-4 p-8">
              <div className="w-20 h-20 bg-green-100 rounded-2xl flex items-center justify-center">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="text-center">
                <p className="text-xl font-semibold text-gray-800">{file.name}</p>
                <p className="text-gray-500 mt-1">
                  {(file.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setFile(null);
                }}
                className="text-sm text-gray-400 hover:text-red-500 transition-colors mt-2"
              >
                Remover e escolher outro
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 p-8">
              <div className={`w-20 h-20 rounded-2xl flex items-center justify-center transition-colors ${isDragging ? 'bg-orca-100' : 'bg-gray-100'}`}>
                <svg className={`w-10 h-10 ${isDragging ? 'text-orca-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <div className="text-center">
                <p className="text-xl font-semibold text-gray-700">
                  {isDragging ? 'Solte o arquivo aqui' : 'Arraste o arquivo aqui'}
                </p>
                <p className="text-gray-400 mt-2">
                  ou <span className="text-orca-600 font-medium">clique para selecionar</span>
                </p>
              </div>
              <p className="text-xs text-gray-400 mt-4">PDF, JPG ou PNG (máx. {MAX_SIZE_MB}MB)</p>
            </div>
          )}
          <input
            id="file-input"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleInputChange}
            className="hidden"
          />
        </label>

        {file && (
          <button
            onClick={handleContinue}
            className="mt-8 w-full bg-orca-800 text-white text-xl font-bold py-5 rounded-2xl hover:bg-orca-700 active:bg-orca-900 transition-all duration-200 shadow-lg shadow-orca-800/20 cursor-pointer"
          >
            Continuar
          </button>
        )}
      </main>
    </div>
  );
}

export default Upload;
